// /src/pages/Home.tsx

import React from 'react';
import type { ServiceFeature, SalesforceFeature } from '../types';
import '../styles/home.css';

const Home: React.FC = () => {
  const serviceFeatures: ServiceFeature[] = [
    {
      icon: 'chart',
      title: 'Strategic Planning',
      description: 'Develop comprehensive business strategies that align with your vision and market opportunities.',
      features: ['Market Analysis & Positioning', 'Growth Strategy Development', 'Competitive Intelligence', 'Strategic Roadmapping']
    },
    {
      icon: 'refresh',
      title: 'Process Excellence',
      description: 'Streamline operations and eliminate inefficiencies through proven methodologies.',
      features: ['Process Mapping & Analysis', 'Workflow Optimization', 'Performance Metrics', 'Change Management']
    },
    {
      icon: 'lightning',
      title: 'Technology Strategy',
      description: 'Leverage technology to enable business transformation.',
      features: ['Platform Strategy & Selection', 'Digital Transformation', 'System Integration Planning', 'Technology Roadmaps']
    }
  ];

  const salesforceFeatures: SalesforceFeature[] = [
    { title: 'Business Process Design', description: 'Custom workflows that align with your unique business requirements' },
    { title: 'Strategic Implementation', description: 'Platform solutions that support long-term business objectives' },
    { title: 'Organizational Adoption', description: 'Change management strategies that ensure successful platform utilization' }
  ];

  return (
    <div>
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>
              Transform Your Business 
              <span className="highlight"> With Strategic Vision</span>
            </h1>
            <p>
              Snowbird Industries delivers expert business consulting and process optimization 
              to help organizations soar to new heights.
            </p>
            <div className="hero-buttons">
              <a href="/contact" className="btn btn-primary btn-large">
                Start Your Transformation
              </a>
              <a href="/services" className="btn btn-outline-white btn-large">
                Learn More
              </a>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="/images/owl-flight.jpg" 
              alt="Snowy owl in flight representing vision and adaptability" 
            />
            <div className="hero-badge">
              <h4>Strategic Vision</h4>
              <p>Like the wise owl, we see opportunities others miss</p>
            </div>
          </div>
        </div>
      </section>

      <section className="services">
        <div className="services-header">
          <h2>Comprehensive Business Solutions</h2>
          <p>
            We partner with organizations to identify opportunities, streamline operations, 
            and implement strategies that drive measurable results.
          </p>
        </div>

        <div className="services-grid">
          {serviceFeatures.map((service: ServiceFeature, index: number) => (
            <div key={service.title} className="service-card">
              <div className={`service-icon ${['blue', 'green', 'purple'][index]}`}>
                {service.icon === 'chart' && '📊'}
                {service.icon === 'refresh' && '🔄'}
                {service.icon === 'lightning' && '⚡'}
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature: string) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="salesforce-section">
        <div className="salesforce-content">
          <div className="salesforce-text">
            <h2>Salesforce Excellence & Business Process Innovation</h2>
            <p>
              Our deep expertise in business process design and platform implementation helps organizations 
              maximize their investment in customer relationship management and business automation.
            </p>
            <div className="salesforce-features">
              {salesforceFeatures.map((feature: SalesforceFeature) => (
                <div key={feature.title} className="salesforce-feature">
                  <div className="feature-icon">✓</div>
                  <div className="feature-content">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="salesforce-image">
            <img 
              src="/images/owl-perched.jpg" 
              alt="Snowy owl perched, representing wisdom and strategic oversight" 
            />
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Transform Your Business?</h2>
          <p>
            Let's discuss how Snowbird Industries can help your organization achieve its strategic objectives 
            through expert consulting and process optimization.
          </p>
          <div className="cta-buttons">
            <a href="/contact" className="btn btn-primary btn-large">
              Schedule a Consultation
            </a>
            <a href="/services" className="btn btn-outline-white btn-large">
              View Our Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;